import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { NeutralColors } from '../../common/colors';
import { ReactComponent as Share } from '../../assets/icons/share.svg';
import { ReactComponent as Copy } from '../../assets/icons/copy.svg';
import { ReactComponent as Mail } from '../../assets/email.svg';
import { ReactComponent as WhatsApp } from '../../assets/social/whatsapp.svg';
import { ReactComponent as Twitter } from '../../assets/social/twitter-x.svg';
import { ReactComponent as LinkedIn } from '../../assets/social/linkedin.svg';
import { screen } from '../../common/breakpoints';
import { VerticalSpacing } from '../../common/spacings';

const PopoverContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-content: center;
  text-align: center;
  margin-right: 0;
  margin-top: ${VerticalSpacing.S};
  z-index: 1;
  @media screen and ${screen.l} {
    margin-top: 0;
  }
`;

const PopoverContent = styled.div<{ isVisible: boolean }>`
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  position: absolute;
  left: 50%;
  right: 0;
  width: 56px;
  bottom: 80px;
  overflow: hidden;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(20px)'};
  transition:
    transform 0.2s ease-in-out,
    opacity 0.2s ease-in-out;
`;

const Backdrop = styled.div<{ isVisible?: boolean }>`
  position: absolute;
  min-width: 100%;
  min-height: 100vh;
  bottom: 0;
  left: 0;
  transition: all 0.3s;
  display: none;
  @media screen and ${screen.l} {
    background: linear-gradient(-179.3deg, rgba(0, 0, 0, 0) 56.63%, rgba(0, 0, 0, 0.5) 103.37%);
    display: ${(props) => (props.isVisible ? 'block' : 'none')};
  }
`;

const PopoverItem = styled.a`
  width: 100%;
  height: 56px;
  border: none;
  margin-bottom: 10px;
  border-radius: 100%;
  background-color: ${NeutralColors.DarkGrey};
  color: ${NeutralColors.White};
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: ${NeutralColors.OffWhite};
  }
`;

const StyledHoverButton = styled.button`
  background-color: ${NeutralColors.Black};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${NeutralColors.White};
  border-radius: 100%;
  cursor: pointer;
  color: ${NeutralColors.White};
  border: 1px solid ${NeutralColors.Black};
  background-color: ${NeutralColors.Black};
  width: 56px;
  height: 56px;
  position: relative;

  &:hover {
    border: 1px solid ${NeutralColors.Black};
    transition: all 0.3s ease;
    background-color: ${NeutralColors.White};
    color: ${NeutralColors.Black};
  }
`;

type Content = {
  id: number;
  icon: ReactElement;
  link?: string;
};

export const SocialPopover = (): ReactElement => {
  const [isVisible, setVisible] = useState(false);
  const { t } = useTranslation();
  const text = t('socialPopover.urlText');

  const content: Content[] = [
    {
      id: 1,
      icon: (
        <Copy
          aria-label={t(`socialPopover.copy.ariaLabel`)}
          title={t(`socialPopover.copy.title`)}
          width="24px"
          height="24px"
          tabIndex={-0}
        />
      ),
    },
    {
      id: 2,
      icon: (
        <Mail
          aria-label={t(`socialPopover.mail.ariaLabel`)}
          title={t(`socialPopover.mail.title`)}
          tabIndex={-1}
        />
      ),
      link: `mailto:?subject=${text}&body=${window.location.href}`,
    },
    {
      id: 3,
      icon: (
        <WhatsApp
          width="24px"
          height="24px"
          tabIndex={-2}
          aria-label={t(`socialPopover.whatsapp.ariaLabel`)}
          title={t(`socialPopover.whatsapp.title`)}
        />
      ),
      link: `https://wa.me/?text=${encodeURIComponent(`${text} ${window.location.href}`)}`,
    },
    {
      id: 4,
      icon: (
        <Twitter
          aria-label={t(`socialPopover.twitter.ariaLabel`)}
          title={t(`socialPopover.twitter.title`)}
          width="24px"
          height="24px"
          tabIndex={-4}
        />
      ),
      link: `https://www.twitter.com/intent/tweet?text=${encodeURIComponent(`${text} ${window.location.href}`)}`,
    },
    {
      id: 5,
      icon: (
        <LinkedIn
          aria-label={t(`socialPopover.linkedin.ariaLabel`)}
          title={t(`socialPopover.linkedin.title`)}
          width="24px"
          height="24px"
          tabIndex={-3}
        />
      ),
      link: `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(`${text} ${window.location.href}`)}`,
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <PopoverContainer onClick={() => setVisible(!isVisible)} data-testid="popover-container">
        <StyledHoverButton>
          <Share
            aria-label={t('socialPopover.ariaLabel')}
            title={t('socialPopover.title')}
            width="24px"
            height="24px"
          />
        </StyledHoverButton>
        <PopoverContent isVisible={isVisible} data-testid="popover-content">
          {content.map((links) =>
            !links.link ? (
              <PopoverItem key={links.id} onClick={copyToClipboard}>
                {links.icon}
              </PopoverItem>
            ) : (
              <PopoverItem key={links.id} href={links.link} target="_blank">
                {links.icon}
              </PopoverItem>
            ),
          )}
        </PopoverContent>
      </PopoverContainer>
      <Backdrop isVisible={isVisible} />
    </>
  );
};
